import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import "./Subtotal.css";
import {
  ItemDetailsData,
  FinancialData,
} from "../../../../Utils/DefinitionsInterfaces";
import { UserContext } from "../../CheckoutPage";
import {
  doNFOTransaction,
  doNFOTransactionRazorpay,
  doNFOTransactionResponse,
  formSparkSubmit,
  razorpaySaveDetails,
  getCouponDiscount,
} from "../../../../Utils/CheckoutAPIs/CheckoutAPIs";
import { Alert } from "@mui/material";
import loader from "../../../../Assets/svgs/loader.svg";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { trackData } from "../../../../Utils/attribution/CheckoutAttribution";
import ReactGA from "react-ga4";

interface SubtotalProps {
  stateCheckoutItems: ItemDetailsData[];
  stateDefaultItems: FinancialData[];
  cbHandlePaymentClick: (orderIds?: doNFOTransactionResponse) => void;
}

interface Error {
  name: string;
  school: string;
  mobileNumber: string;
  email: string;
  grade: string;
  pincode: string;
  city: string;
  state: string;
}

interface CouponState {
  code: string;
  isApplied: boolean;
  isLoading: boolean;
  discountAmount: number;
  originalAmount: number;
  finalAmount: number;
  error: string;
}

const Subtotal = (props: SubtotalProps) => {
  let currentItemIds: number[] = [];
  for (let i = 0; i < props.stateCheckoutItems.length; i++) {
    currentItemIds.push(props.stateCheckoutItems[i].id);
  }
  const userContext = useContext(UserContext);
  const userDetails = userContext?.userDetails ?? {
    name: "",
    email: "",
    school: "",
    schoolID: 0,
    grade: 0,
    mobileNumber: "",
    address: "",
    flatNo: "",
    city: "",
    state: "",
    pincode: "",
    pfh: false,
  };
  const individualReg = userContext?.individualReg ?? false;
  const isOtpVerified = userContext?.isOtpVerified ?? false;
  const backendSchool = userContext?.backendSchool ?? {
    schoolID: 0,
    schoolName: "",
    linkActive: false,
    city: "",
    state: "",
    individual_registration: false,
    examDate: "",
    isInternational: false,
    version_no: "V1",
    pg: "razorpay",
  };

  const isPFH = userContext?.pfhVerified ?? false;
  const [stateCurrentItemIds, setStateCurrentItemIds] =
    useState<number[]>(currentItemIds);
  const [showFormSparkSuccess, setShowFormSparkSuccess] = useState(false);

  const [isPurchaseButtonClicked, setIsPurchaseButtonClicked] = useState(false);
  const referredBy = sessionStorage.getItem("referredBy");

  const [error, setError] = useState<Partial<Error>>({});
  const navigate = useNavigate();
  const schoolCode = sessionStorage.getItem("schoolCode");

  const [showAlert, setShowAlert] = useState({
    isAlertVisible: false,
    alert: "",
  });

  // Coupon state variables
  const [couponState, setCouponState] = useState<CouponState>({
    code: "",
    isApplied: false,
    isLoading: false,
    discountAmount: 0,
    originalAmount: 0,
    finalAmount: 0,
    error: "",
  });

  const [couponInput, setCouponInput] = useState("");

  // Ref to track if auto-apply has been attempted to prevent infinite loops
  const autoApplyAttempted = useRef(false);

  // Check if we should disable physical book experiment (rollNumber + autoApplyRollAsCoupon)
  const shouldDisablePhysicalBook = () => {
    const rollNumber = sessionStorage.getItem("rollNumber");
    const hasAutoApplyCoupon = props.stateDefaultItems.some(
      (item) => item.autoApplyRollAsCoupon === true
    );
    return rollNumber && hasAutoApplyCoupon;
  };

  // Dynamic field validation based on whether address forms are hidden
  const getRequiredFields = () => {
    const baseKeys = [
      "name",
      "email",
      "mobileNumber",
      "school",
      "schoolID",
      "grade",
    ];
    const baseFieldNames = [
      "name",
      "email",
      "phone",
      "school",
      "schoolID",
      "grade",
    ];

    if (shouldDisablePhysicalBook()) {
      // Address forms are hidden, only check base fields
      return { keys: baseKeys, fieldNames: baseFieldNames };
    } else {
      // Address forms are visible, check all fields
      const allKeys = [...baseKeys, "pincode", "city", "state"];
      const allFieldNames = [...baseFieldNames, "pincode", "city", "state"];
      return { keys: allKeys, fieldNames: allFieldNames };
    }
  };

  const { keys, fieldNames } = getRequiredFields();
  const values = keys.map((key) => sessionStorage.getItem(key));
  const missingFields = fieldNames.filter((_, index) => !values[index]);

  const handleFormSparkSubmit = async () => {
    const response = await formSparkSubmit(userDetails);
    setIsPurchaseButtonClicked(true);
    if (response.status === 200) {
      setIsPurchaseButtonClicked(false);
      setShowFormSparkSuccess(true);
    } else {
      setIsPurchaseButtonClicked(false);
      setShowAlert({
        isAlertVisible: true,
        alert: "Oops! Something went wrong. Please try again later.",
      });
    }
  };

  // Calculate total amount from both stateDefaultItems and stateCheckoutItems
  const calculateTotalAmount = (): number => {
    let total = 0;

    // Add default items (main NFO registration)
    for (let i = 0; i < props.stateDefaultItems.length; i++) {
      total += props.stateDefaultItems[i].selling_price || 0;
    }

    // Add checkout items (additional items)
    for (let i = 0; i < props.stateCheckoutItems.length; i++) {
      total += props.stateCheckoutItems[i].selling_price || 0;
    }

    return total;
  };

  // Handle coupon application
  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) {
      setCouponState((prev) => ({
        ...prev,
        error: "Please enter a coupon code",
      }));
      return;
    }

    setCouponState((prev) => ({ ...prev, isLoading: true, error: "" }));

    try {
      const currentAmount = calculateTotalAmount();
      const response = await getCouponDiscount(
        couponInput.trim(),
        currentAmount,
        false // Manual coupon application, not auto-apply
      );

      if (response.status === "Success") {
        setCouponState({
          code: response.data.coupon_code,
          isApplied: true,
          isLoading: false,
          discountAmount: response.data.discount_amount,
          originalAmount: response.data.priceBeforeDiscount,
          finalAmount: response.data.priceAfterDiscount,
          error: "",
        });

        // Store in session storage for payment success page
        sessionStorage.setItem("appliedCouponCode", response.data.coupon_code);
        sessionStorage.setItem(
          "couponDiscountAmount",
          response.data.discount_amount.toString()
        );
        sessionStorage.setItem(
          "originalAmount",
          response.data.priceBeforeDiscount.toString()
        );
      }
    } catch (error: any) {
      setCouponState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.response?.data?.message || "Invalid coupon code",
      }));
    }
  };

  // Handle coupon removal
  const handleRemoveCoupon = () => {
    setCouponState({
      code: "",
      isApplied: false,
      isLoading: false,
      discountAmount: 0,
      originalAmount: 0,
      finalAmount: 0,
      error: "",
    });

    setCouponInput("");

    // Clear session storage
    sessionStorage.removeItem("appliedCouponCode");
    sessionStorage.removeItem("couponDiscountAmount");
    sessionStorage.removeItem("originalAmount");
  };

  // Auto-apply coupon from URL parameter or rollNumber
  const autoApplyCouponFromUrl = useCallback(async () => {
    if (couponState.isApplied || autoApplyAttempted.current) {
      return; // Coupon already applied or already attempted
    }

    // Check if auto-apply is enabled for any default item
    const shouldAutoApply = props.stateDefaultItems.some(
      (item) => item.autoApplyRollAsCoupon === true
    );

    if (!shouldAutoApply) {
      console.log("Auto-apply coupon disabled for this registration");
      autoApplyAttempted.current = true; // Mark as attempted to prevent re-checking
      return;
    }

    // Try URL coupon first, then rollNumber as coupon
    const urlCouponCode = sessionStorage.getItem("urlCouponCode");
    const rollNumber = sessionStorage.getItem("rollNumber");
    const couponToApply = urlCouponCode || rollNumber;

    if (!couponToApply) {
      console.log("No coupon code or rollNumber available for auto-apply");
      autoApplyAttempted.current = true;
      return;
    }

    console.log(
      `Auto-applying coupon: ${couponToApply} (from ${
        urlCouponCode ? "URL" : "rollNumber"
      })`
    );
    autoApplyAttempted.current = true; // Mark as attempted immediately
    setCouponInput(couponToApply);
    setCouponState((prev) => ({ ...prev, isLoading: true, error: "" }));

    try {
      const currentAmount = calculateTotalAmount();
      const response = await getCouponDiscount(
        couponToApply,
        currentAmount,
        true
      );

      if (response.status === "Success") {
        setCouponState({
          code: response.data.coupon_code,
          isApplied: true,
          isLoading: false,
          discountAmount: response.data.discount_amount,
          originalAmount: response.data.priceBeforeDiscount,
          finalAmount: response.data.priceAfterDiscount,
          error: "",
        });

        // Store in session storage
        sessionStorage.setItem("appliedCouponCode", response.data.coupon_code);
        sessionStorage.setItem(
          "couponDiscountAmount",
          response.data.discount_amount.toString()
        );
        sessionStorage.setItem(
          "originalAmount",
          response.data.priceBeforeDiscount.toString()
        );

        console.log(
          `Coupon "${response.data.coupon_code}" auto-applied! Saved ₹${response.data.discount_amount}`
        );

        // Remove the URL coupon from session storage to prevent re-application
        if (urlCouponCode) {
          sessionStorage.removeItem("urlCouponCode");
        }
      }
    } catch (error: any) {
      console.error("Auto-apply coupon failed:", error);
      setCouponState((prev) => ({
        ...prev,
        isLoading: false,
        error: "", // Don't show error for auto-apply failures
      }));
    }
  }, [couponState.isApplied, props.stateDefaultItems]);

  // Get charge amount (either discounted or original)
  const getChargeAmount = (): number => {
    return couponState.isApplied
      ? couponState.finalAmount
      : calculateTotalAmount();
  };

  const PurchaseNFO = async () => {
    const emailValidation =
      /^([A-Za-z0-9_\-\.])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,4})$/;
    const mobileValidation = /^[1-9]{1}[0-9]{9}$/;
    const dubaiMobileValidation =
      /^((\+971|971|0)?(50|51|52|54|55|56|58|4))\d{7}$/;

    const alertText =
      "There appear to be errors in the details provided. Please review the form and resubmit. Thank you.";
    let flag = 0;
    if (userDetails.name.length < 3) {
      flag = 1;
      console.log("Name problem");
      setShowAlert({
        isAlertVisible: true,
        alert: "The given name is invalid. Please check the given name.",
      });
    } else if (userDetails.school.length < 3) {
      flag = 1;
      console.log("School problem: userdetails = ", userDetails);
      setShowAlert({
        isAlertVisible: true,
        alert: "Please enter a valid school name.",
      });
    } else if (
      !(backendSchool.isInternational
        ? dubaiMobileValidation.test(userDetails.mobileNumber || "")
        : mobileValidation.test(userDetails.mobileNumber || ""))
    ) {
      console.log("mobile number problem", userDetails.mobileNumber);
      flag = 1;
      setShowAlert({
        isAlertVisible: true,
        alert: "Please enter a valid mobile number.",
      });
    } else if (userDetails.grade < 5 || userDetails.grade > 12) {
      console.log("Grade problem");
      flag = 1;
      setShowAlert({
        isAlertVisible: true,
        alert: "Please enter a valid grade.",
      });
    } else if (!emailValidation.test(userDetails.email)) {
      flag = 1;
      console.log("email problem");
      setShowAlert({
        isAlertVisible: true,
        alert: "Please enter a valid email.",
      });
    } else if (
      !shouldDisablePhysicalBook() &&
      userDetails.pincode.length !== 6
    ) {
      flag = 1;
      console.log("pincode problem");
      setShowAlert({
        isAlertVisible: true,
        alert: "Please enter a valid pincode.",
      });
    } else if (!shouldDisablePhysicalBook() && userDetails.city.length < 3) {
      flag = 1;
      console.log("city problem");
      setShowAlert({
        isAlertVisible: true,
        alert: "Please enter a valid city.",
      });
    } else if (!shouldDisablePhysicalBook() && userDetails.state.length < 3) {
      flag = 1;
      console.log("state problem");
      setShowAlert({
        isAlertVisible: true,
        alert: "Please enter a valid state.",
      });
    } else if (missingFields.length > 0) {
      flag = 1;
      console.log(missingFields);
      setShowAlert({
        isAlertVisible: true,
        alert: `Please fill in the following required fields: ${missingFields.join(
          ", "
        )}`,
      });
    } else if (!isOtpVerified) {
      flag = 1;
      setShowAlert({
        isAlertVisible: true,
        alert: "Please verify your OTP.",
      });
    } else {
      flag = 0;
      setError({
        name: "",
        school: "",
        grade: "",
        mobileNumber: "",
        email: "",
        pincode: "",
        city: "",
        state: "",
      });
    }

    if (flag === 0 && isOtpVerified) {
      trackData(
        "user_clicks_on_purchase_button",
        userDetails.name,
        userDetails.mobileNumber,
        {
          payment_type: "prepaid",
          sale: {
            product_type: "nfo_register_success",
            amount_inr: getChargeAmount(),
            order_id: undefined,
          },
        }
      );
      setIsPurchaseButtonClicked(true);
      if (backendSchool.isInternational) {
        props.cbHandlePaymentClick();
      } else {
        console.log(
          "this is happening",
          userDetails.pincode,
          userDetails.city,
          userDetails.state
        );
        const pg = sessionStorage.getItem("pg");
        console.log(
          "payload",
          userDetails.name,
          userDetails.schoolID.toString(),
          userDetails.grade,
          userDetails.mobileNumber,
          userDetails.email,
          stateCurrentItemIds,
          userDetails.school,
          individualReg,
          referredBy,
          userDetails.pincode,
          userDetails.city,
          userDetails.state,
          isPFH,
          sessionStorage.getItem("nfo2024") === "true",
          couponState.isApplied ? couponState.code : undefined
        );
        if (pg === "razorpay") {
          doNFOTransactionRazorpay(
            userDetails.name,
            userDetails.schoolID.toString(),
            userDetails.grade,
            userDetails.mobileNumber,
            userDetails.email,
            stateCurrentItemIds,
            userDetails.school,
            individualReg,
            referredBy,
            userDetails.pincode,
            userDetails.city,
            userDetails.state,
            isPFH,
            sessionStorage.getItem("nfo2024") === "true",
            couponState.isApplied ? couponState.code : undefined
          )
            .then((response) => {
              //@ts-ignore
              if (response.data.status === "Success") {
                const {
                  amount,
                  currency,
                  id,
                  notes,

                  //@ts-ignore
                } = response.data.message;
                sessionStorage.setItem(
                  "transactionAmount",
                  (amount / 100).toString()
                );
                ReactGA.event({
                  category: "5678_checkout_register",
                  action: "5678_checkout_register",
                  label: "5678_checkout_register",
                  value: amount,
                });
                trackData(
                  "initiate_checkout",
                  userDetails.name,
                  userDetails.mobileNumber,
                  {
                    payment_type: "prepaid",
                    sale: {
                      product_type: "nfo_register_success",
                      amount_inr: Number(amount / 100),
                      order_id: id,
                    },
                  }
                );
                // Step 2: Open Razorpay Checkout
                const options = {
                  key: "rzp_live_B6xhot5HoXKLQE", // Replace with your Razorpay API Key
                  amount: amount, // Amount in paise
                  currency: currency,
                  name: "National Finance Olympiad & Study Material",
                  description: "National Finance Olympiad & Study Material",
                  image:
                    "https://cdn.razorpay.com/logos/BUVwvgaqVByGp2_large.jpg",
                  order_id: id, // Pass the order_id received from your API
                  prefill: {
                    name: notes.name,
                    email: notes.email,
                    contact: notes.mobileNumber,
                  },
                  theme: {
                    color: "rgba(55, 108, 170, 1)",
                  },
                  method: {
                    netbanking: true,
                    card: true,
                    wallet: false,
                    upi: true,
                    paylater: false,
                  },
                  handler: async (response: any) => {
                    const response2 = await razorpaySaveDetails(
                      response.razorpay_payment_id,
                      id,
                      response.razorpay_signature
                    );

                    navigate(
                      `/PaymentSuccessful/?&scl=${
                        schoolCode ?? ""
                      }&order_id=${id}&${
                        notes.choosenItemIDs.length > 0
                          ? `item_ids=${notes.choosenItemIDs}`
                          : ""
                      }`
                    );
                  },
                  modal: {
                    ondismiss: function () {
                      // navigate(
                      //   `/CheckoutPage/?&scl=${
                      //     backendSchool.schoolCode
                      //   }&order_id=${id}&${
                      //     notes.choosenItemIDs.length > 0
                      //       ? `item_ids=${notes.choosenItemIDs}`
                      //       : ""
                      //   }`
                      // );
                      console.log("this is ondismiss");
                    },
                  },
                };

                const razorpay = new (window as any).Razorpay(options);
                razorpay.open();

                //@ts-ignore
                console.log("this is response = ", response.data.message.id);

                setIsPurchaseButtonClicked(false);
              }
            })
            .catch((err) => {
              setIsPurchaseButtonClicked(false);
              console.log(err);
              Sentry.captureException(err);
              setShowAlert({
                isAlertVisible: true,
                alert: "Oops! Something went wrong. Please try again later.",
              });
            });
        } else {
          doNFOTransaction(
            userDetails.name,
            userDetails.schoolID.toString(),
            userDetails.grade,
            userDetails.mobileNumber,
            userDetails.email,
            stateCurrentItemIds,
            userDetails.school,
            individualReg,
            referredBy,
            sessionStorage.getItem("nfo2024") === "true"
          )
            .then((response) => {
              console.log("this is response = ", response);
              //@ts-ignore
              if (response.data.status === "Success") {
                if (
                  //@ts-ignore
                  response.data.data.orderID
                ) {
                  //@ts-ignore
                  props.cbHandlePaymentClick(response.data);
                  console.log("this is happening");
                  setIsPurchaseButtonClicked(false);
                }
              } else {
                // setLoading(false);
                setIsPurchaseButtonClicked(false);
                //@ts-ignore
                console.log(response.data.message);
                //@ts-ignore
                Sentry.captureException(
                  //@ts-ignore
                  "Error in doNFOTransaction = " + response.data.message
                );

                setShowAlert({
                  isAlertVisible: true,
                  alert: "Oops! Something went wrong. Please try again later.",
                });
              }
            })
            .catch((err) => {
              // setLoading(false);
              setIsPurchaseButtonClicked(false);

              console.log(err);
              Sentry.captureException(
                "Error in doNFOTransaction = " + err.response.data
              );

              setShowAlert({
                isAlertVisible: true,
                alert: "Oops! Something went wrong. Please try again later.",
              });
            });
        }
      }
    }
  };

  useEffect(() => {
    if (showAlert.alert) {
      setTimeout(() => {
        setShowAlert({
          isAlertVisible: false,
          alert: "",
        });
      }, 5000);
    }
    if (showFormSparkSuccess) {
      setTimeout(() => {
        setShowFormSparkSuccess(false);
      }, 5000);
    }
  }, [showAlert.alert, showFormSparkSuccess]);

  // Auto-apply coupon from URL parameter when component loads and items are available
  useEffect(() => {
    if (
      props.stateDefaultItems.length > 0 &&
      !couponState.isApplied &&
      !autoApplyAttempted.current
    ) {
      const totalAmount = calculateTotalAmount();
      if (totalAmount > 0) {
        autoApplyCouponFromUrl();
      }
    }
  }, [
    props.stateDefaultItems,
    couponState.isApplied,
    calculateTotalAmount,
    autoApplyCouponFromUrl,
  ]);

  return (
    <div className="subtotal">
      <div className="group-2">
        {/* Coupon input section - only show for individual registration */}
        {individualReg && (
          <div className="coupon-section">
            <div className="coupon-input-wrapper">
              <input
                type="text"
                placeholder="Enter coupon code"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                className="coupon-input"
                disabled={couponState.isApplied}
              />
              <button
                onClick={handleApplyCoupon}
                disabled={couponState.isLoading || couponState.isApplied}
                className="apply-coupon-button"
              >
                {couponState.isLoading ? "Applying..." : "Apply"}
              </button>
            </div>

            {/* Coupon success display */}
            {couponState.isApplied && (
              <div className="coupon-success">
                <div className="coupon-success-header">
                  <div className="success-icon">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm3.5 6.1l-4.4 4.4c-.3.3-.8.3-1.1 0L3.5 8c-.3-.3-.3-.8 0-1.1s.8-.3 1.1 0L6 8.3l3.4-3.4c.3-.3.8-.3 1.1 0s.3.8 0 1.2z"
                        fill="#376CAA"
                      />
                    </svg>
                  </div>
                  <span className="success-text">
                    Coupon <strong>"{couponState.code}"</strong> applied
                    successfully!
                  </span>
                  <button
                    onClick={handleRemoveCoupon}
                    className="remove-coupon-icon"
                    title="Remove coupon"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"
                        fill="#666"
                      />
                    </svg>
                  </button>
                </div>
                <div className="price-breakdown">
                  <div className="price-row discount-row">
                    <span className="price-label">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M7 0C3.1 0 0 3.1 0 7s3.1 7 7 7 7-3.1 7-7-3.1-7-7-7zm3.5 7.5h-7v-1h7v1z"
                          fill="#376CAA"
                        />
                      </svg>
                      Discount:
                    </span>
                    <span className="price-value discount">
                      -₹{couponState.discountAmount}
                    </span>
                  </div>
                  <div className="price-row final-row">
                    <span className="price-label">Final Amount:</span>
                    <span className="price-value final">
                      ₹{couponState.finalAmount}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Coupon error display */}
            {couponState.error && (
              <div className="coupon-error">
                <div className="error-content">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm4.3 10.7L10.7 12.3 8 9.6l-2.7 2.7L3.7 10.7 6.4 8 3.7 5.3 5.3 3.7 8 6.4l2.7-2.7 1.6 1.6L9.6 8l2.7 2.7z"
                      fill="#D32F2F"
                    />
                  </svg>
                  <span>{couponState.error}</span>
                </div>
              </div>
            )}
          </div>
        )}

        <button
          className="subtotal-button"
          onClick={() => {
            PurchaseNFO();
          }}
          disabled={isPurchaseButtonClicked}
        >
          {!isPurchaseButtonClicked && (
            <div className="text-wrapper-11">Register Now</div>
          )}
          {isPurchaseButtonClicked && (
            <img className="text-wrapper-11" src={loader} alt="O" />
          )}
        </button>
      </div>

      {showAlert.alert && (
        <Alert
          style={{
            borderRadius: 10,
            opacity: 1,
          }}
          severity="error"
        >
          {showAlert.alert}
        </Alert>
      )}
      {showFormSparkSuccess && (
        <Alert
          style={{
            borderRadius: 10,
            opacity: 1,
          }}
          severity="info"
        >
          Thank you for showing interest in the National Finance Olympiad.We
          will contact you once registrations are open.
        </Alert>
      )}
    </div>
  );
};

export default Subtotal;
